import { useCallback, useMemo, useState } from "react"
import { PageTitle } from "../../../_metronic/layout/core"
import PreloaderIcon from "../../components/PreloaderIcon"
import { Card } from "../../ui/Card"
import { Text } from "../../ui/Text"
import { Heading } from "../../ui/Heading"
import { Stack } from "../../ui/Stack"
import { useEffectOnce } from "../../hooks/useEffectOnce"
import { getVendaAi } from "../../api/copiloto-do-vendedor"
import { RawVendaAiRecommendation } from "../../types/RawVendaAiRecommendation"
import { formatMoney, formatCNPJ, convertToBrazilianDate } from "../../utils/format"
import { Table, TableRenderItemOptions } from "../../components/Table"
import { useNavigate } from "react-router-dom";
import { RoutePath } from "../../routing/routes";
import { DollarSign, Eye, Clock, Sparkles, AlertTriangle } from 'lucide-react';

export const CopilotoDoVendedorVendaAI = () => {

    const [stores, setStores] = useState<RawVendaAiRecommendation[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [responseType, setResponseType] = useState<'processing' | 'no-data' | 'data'>('processing');

    const navigate = useNavigate();

    const total = useMemo(() => {
        return stores.reduce((total, store) => total + (Number(store.PredicaoSellOut) || 0), 0);
    }, [stores]);

    const loadData = useCallback(async () => {
        const res:any = await getVendaAi();
        const data = res.data || [];
        setStores(data);

        if (res.type === 'processing') {
            setResponseType('processing');
        } else if (res.type === 'no-data') {
            setResponseType('no-data');
        } else if (data.length > 0) {
            setResponseType('data');
        }

        setIsLoading(false);
    }, []);

    const navigateIntoStore = (cnpj:string) => {
        const path:RoutePath = '/copiloto-do-vendedor/lojista';
        navigate(`${path}/${cnpj}`);
    }

    useEffectOnce(() => {
        loadData();
    });

    const handleRenderItem = useCallback(({item, key, index}:TableRenderItemOptions<RawVendaAiRecommendation>) => {
        if (key === 'PredicaoSellOut') {
            return <Text>{formatMoney(item.PredicaoSellOut)}</Text>
        }
        else if (key === 'DataReferencia') {
            return <Text>{convertToBrazilianDate(item.DataReferencia, false)}</Text>
        }
        else if (key === 'IDLojista') {
            return <Text>{item.IDLojista}</Text>
        }
        else if (key === 'CNPJLojista') {
            return <Text>{formatCNPJ(item.CNPJLojista)}</Text>
        }
        else if (key === 'RazaoSocial') {
            return <Text>{item.RazaoSocial}</Text>
        }
        else if (key === 'NomeFantasia') {
            return <div className="flex items-center">
                <Text>{item.NomeFantasia}</Text>
            </div>
        }

        else if (key === 'View') {
            return (
                <button onClick={() => { navigateIntoStore(item.CNPJLojista)}} className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                  <Eye className="h-4 w-4 mr-1" />
                  Visualizar
                </button>
              );
        }

        return null;
    }, []);

    return <>
        <PageTitle>{`Copiloto do vendedor > Venda AI`}</PageTitle>

        <Stack>
            { isLoading ? <>
                <Card>
                    <Stack alignItems="center">
                        <Heading>Procurando por oportunidades</Heading>
                        <PreloaderIcon />
                    </Stack>
                </Card>
            </> : responseType === 'processing' ? <>
                <div className="relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 opacity-70"></div>

                    <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-400/10 to-purple-400/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
                        <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-indigo-400/10 to-blue-400/10 rounded-full -ml-32 -mb-32 blur-3xl"></div>

                        <div className="relative px-8 py-16 md:px-16 md:py-20">
                            <div className="max-w-3xl mx-auto text-center">
                                <div className="flex justify-center mb-8">
                                    <div className="relative">
                                        <div className="absolute inset-0 rounded-full blur-2xl opacity-30 animate-pulse"></div>
                                        <div className="relative bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full p-6 shadow-xl">
                                            <Clock className="h-16 w-16 text-white" strokeWidth={1.5} />
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-6">
                                    <h2 className="text-2xl md:text-2xl font-bold bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-600 bg-clip-text text-transparent mb-4">
                                        Olá, Vendedor!
                                    </h2>
                                    <div className="flex items-center justify-center gap-2 mb-6">
                                        <div className="h-1 w-12 bg-gradient-to-r from-transparent via-blue-500 to-transparent rounded-full"></div>
                                        <AlertTriangle className="h-5 w-5 text-blue-500" />
                                        <div className="h-1 w-12 bg-gradient-to-r from-transparent via-blue-500 to-transparent rounded-full"></div>
                                    </div>
                                </div>

                                <div className="space-y-4 mb-8">
                                    <p className="text-xl md:text-xl font-semibold text-gray-800 leading-relaxed">
                                        Nosso modelo de IA está trabalhando para trazer
                                    </p>
                                    <p className="text-xl md:text-xl font-semibold text-gray-800 leading-relaxed">
                                        os <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">melhores dados</span> deste mês para você
                                    </p>
                                </div>
                                <div className="mt-10 pt-8 border-t border-gray-200">
                                    <p className="text-sm text-gray-500 flex items-center justify-center gap-2">
                                        <Sparkles className="h-4 w-4 text-indigo-400" />
                                        Estamos preparando insights valiosos para você
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </> : responseType === 'no-data' ? <>
                <div className="relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 opacity-70"></div>

                    <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-amber-400/10 to-orange-400/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
                        <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-orange-400/10 to-red-400/10 rounded-full -ml-32 -mb-32 blur-3xl"></div>

                        <div className="relative px-8 py-16 md:px-16 md:py-20">
                            <div className="max-w-3xl mx-auto text-center">
                                <div className="flex justify-center mb-8">
                                    <div className="relative">
                                        <div className="absolute inset-0 rounded-full blur-2xl opacity-30 animate-pulse"></div>
                                        <div className="relative bg-gradient-to-br from-amber-500 to-orange-600 rounded-full p-6 shadow-xl">
                                            <AlertTriangle className="h-16 w-16 text-white" strokeWidth={1.5} />
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-6">
                                    <h2 className="text-2xl md:text-2xl font-bold bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 bg-clip-text text-transparent mb-4">
                                        Atenção, Vendedor!
                                    </h2>
                                    <div className="flex items-center justify-center gap-2 mb-6">
                                        <div className="h-1 w-12 bg-gradient-to-r from-transparent via-amber-500 to-transparent rounded-full"></div>
                                        <AlertTriangle className="h-5 w-5 text-orange-500" />
                                        <div className="h-1 w-12 bg-gradient-to-r from-transparent via-orange-500 to-transparent rounded-full"></div>
                                    </div>
                                </div>

                                <div className="space-y-4 mb-8">
                                    <p className="text-xl md:text-xl font-semibold text-gray-800 leading-relaxed">
                                        Ainda não há dados suficientes para
                                    </p>
                                    <p className="text-xl md:text-xl font-semibold text-gray-800 leading-relaxed">
                                        realizar <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-orange-600">predição da sua carteira</span>
                                    </p>
                                </div>

                                

                                <div className="mt-10 pt-8 border-t border-gray-200">
                                    <p className="text-sm text-gray-500 flex items-center justify-center gap-2">
                                        <Sparkles className="h-4 w-4 text-orange-400" />
                                        Precisamos de mais dados para gerar insights precisos
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </> : <>
                <Card>
                    <Stack>
                        <div className="bg-white rounded-lg shadow p-6 mb-4">
                            <div className="flex items-center space-x-3">
                                <div className="flex-shrink-0">
                                    <div className="bg-green-100 rounded-full p-3">
                                        <DollarSign className="h-6 w-6 text-green-600" />
                                    </div>
                                </div>
                                <div>
                                    <p className="text-gray-600">
                                        Você está deixando de vender{' '}
                                        <span className="font-semibold text-gray-900">
                                        {formatMoney(total)}
                                        </span>{' '}
                                        no mês.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <Table<RawVendaAiRecommendation> schema={[
                            { key: 'CNPJLojista', label: 'CNPJ'},
                            { key: 'RazaoSocial', label: 'Razão Social'},
                            { key: 'NomeFantasia', label: 'Nome Fantasia'},


                            { key: 'PredicaoSellOut', label: 'Potencial de Vendas'},
                            { key: 'DataReferencia', label: 'Data referência'},
                            { key: 'View', label: 'Visualizar'},
                        ]}
                        data={stores}
                        onRenderItem={handleRenderItem}
                        formats={[
                            {key:'CNPJLojista', type:'cnpj'},
                            {key:'PredicaoSellOut', type:'money'},
                            {key:'DataReferencia', type:'date'},
                        ]}
                        />
                    </Stack>
                </Card>
            </> }
        </Stack>
    </>
}
